/**
 * GameContainer — wraps the user's GameScene and runs the multiplayer loop.
 *
 * Responsibilities:
 *   - Construct the user's GameScene as a child scene.
 *   - Drive the fixed-timestep simulation via Tick.
 *   - Host: drain inputs, call `gameScene.tick(dt, inputs)`, broadcast snapshots.
 *   - Client: collect local inputs, send them to host, apply incoming snapshots.
 *   - Wire pause / visibility / reconnection / latency.
 *   - Detect "game over" via the user-set `gameScene.matchResult` and switch
 *     to GameOver.
 *
 * The user's GameScene must extend `SdkGameScene` (see `../index.js`).
 */
import { Tick } from '../network/Tick.js';
import { Random } from '../seed/Random.js';
import { Theme } from '../ui/Theme.js';
import { Toast } from '../ui/Toast.js';
import { Modal } from '../ui/Modal.js';

export class GameContainer extends Phaser.Scene {
  constructor() {
    super('GameContainer');
  }

  init(data) {
    this.welcome = data || { seed: '0', players: [], settings: {} };
  }

  create() {
    const reg = this.registry;
    this.manifest = reg.get('manifest');
    this.net = reg.get('network');
    this.sync = reg.get('sync');
    this.audio = reg.get('audio');
    // NOTE: do not assign to `this.input` — Phaser's scene input plugin
    // already lives there. The SDK Input is exposed as `this.sdkInput`.
    this.sdkInput = reg.get('input');
    this.fps = reg.get('fpsMonitor');
    this.qualityScaler = reg.get('qualityScaler');
    this.telemetry = reg.get('telemetry');
    this.replay = reg.get('replay');
    this.GameSceneClass = reg.get('GameSceneClass');

    this.toast = new Toast(this);
    this.cameras.main.setBackgroundColor(Theme.colors.bg);

    // Per-match deterministic RNG, shared by host + clients.
    this.rng = new Random(this.welcome.seed);

    // Simulation mode + local identity for the active match. Read once
    // here so the hot path in `_step()` is a single field comparison.
    this.simMode = (this.manifest.network && this.manifest.network.simMode) || 'synchronized';
    this.localPlayerId = this.net.isHost ? 'host' : this.net.playerId;

    // Multi-subscriber event bus shared with the user scene via sdk.on().
    /** @type {Map<string, Array<(payload: any) => void>>} */
    this._eventListeners = new Map();

    // Recording (host only).
    if (this.net.isHost && this.manifest.features && this.manifest.features.replay) {
      this.replay.beginRecording(this.welcome.seed);
    }

    // Spin up the user's GameScene as a real Phaser child scene so it can
    // use sprites/groups/physics naturally.
    if (!this.scene.manager.keys['UserGame']) {
      this.scene.add('UserGame', this.GameSceneClass, false);
    }
    this.scene.launch('UserGame', {
      players: this.welcome.players,
      seed: this.welcome.seed,
      settings: this.welcome.settings,
      sdk: this._sdkHandleForUserScene()
    });
    this.userScene = this.scene.manager.getScene('UserGame');

    // Fixed-timestep tick driver.
    const tickRate = (this.manifest.network && this.manifest.network.tickRateHz) || 20;
    this.tick = new Tick({
      tickRateHz: tickRate,
      onStep: (stepIndex, dtSec) => this._step(stepIndex, dtSec)
    });

    // Pre-match "3, 2, 1, GO!" countdown. Gives every peer a visible
    // sync window — they all see the same count from their own
    // receive-time, so the tick starts within ~100ms on every player.
    // Also masks scene-init jitter so the first second of gameplay
    // doesn't look accelerated on slow devices.
    // Opt out via manifest.features.startCountdown === false.
    const doCountdown = !(this.manifest.features && this.manifest.features.startCountdown === false);
    if (doCountdown) {
      this._runStartCountdown(() => this.tick.start());
    } else {
      this.tick.start();
    }

    this._lastSnapshotEmit = performance.now();

    // Bind the sync layer to events emitted while in-game.
    this.sync.onEvent = (name, payload) => {
      if (name === 'match-end') this._endMatch(payload);
      // Dispatch to any sdk.on(name, cb) subscribers (Leaderboard etc.)…
      const subs = this._eventListeners.get(name);
      if (subs) {
        for (const cb of subs.slice()) {
          try { cb(payload); } catch (err) {
            if (typeof console !== 'undefined') console.error('[sdk.on subscriber]', err);
          }
        }
      }
      // …and still call the legacy single-slot user hook.
      if (this.userScene && this.userScene.onSdkEvent) {
        this.userScene.onSdkEvent(name, payload);
      }
    };

    if (!this.net.isHost) {
      this.sync.onSnapshot = (msg) => {
        if (this.userScene && this.userScene.applySnapshot) {
          this.userScene.applySnapshot(msg.snap);
        }
      };
    }

    // ESC / pause action.
    this.events.on('update', () => {
      if (this.sdkInput.action('pause') && !this._paused && !this._pausePressLatch) {
        this._pausePressLatch = true;
        this._togglePause();
      }
      if (!this.sdkInput.action('pause')) this._pausePressLatch = false;
    });

    this.telemetry.emit('match-started', { gameId: this.manifest.id, seed: this.welcome.seed });
  }

  /** API surface exposed to the user's GameScene as `this.sdk`. */
  _sdkHandleForUserScene() {
    const reg = this.registry;
    return {
      isHost: this.net.isHost,
      localPlayerId: this.localPlayerId,
      simMode: this.simMode,
      players: this.welcome.players.slice(),
      settings: Object.assign({}, this.welcome.settings),
      seed: this.welcome.seed,
      audio: this.audio,
      input: this.sdkInput,
      i18n: reg.get('i18n'),
      qualityScaler: this.qualityScaler,
      rng: this.rng,

      // AR / sensors / tracking
      camera: reg.get('camera'),
      orientation: reg.get('orientationSource'),
      motion: reg.get('motionSource'),
      tracking: reg.get('tracking'),

      // Screenshot helper (Phaser canvas → Blob / data URL / download)
      screenshot: reg.get('screenshot'),

      // Helpers the game uses to broadcast / subscribe / end:
      emit: (name, payload) => this.sync.emitEvent(name, payload),
      on: (name, cb) => this._addListener(name, cb),
      endMatch: (result) => {
        if (!this.net.isHost) return;
        this.sync.emitEvent('match-end', result || {});
        this._endMatch(result);
      }
    };
  }

  /** Multi-subscriber registration for sdk.on(name, cb). */
  _addListener(name, cb) {
    if (typeof cb !== 'function') return () => {};
    let arr = this._eventListeners.get(name);
    if (!arr) {
      arr = [];
      this._eventListeners.set(name, arr);
    }
    arr.push(cb);
    return () => {
      const list = this._eventListeners.get(name);
      if (!list) return;
      const i = list.indexOf(cb);
      if (i !== -1) list.splice(i, 1);
    };
  }

  update(time, delta) {
    this.fps.sample(delta);
    this.qualityScaler.update(delta);
    if (!this._paused) this.tick.advance(delta);
  }

  _step(stepIndex, dtSec) {
    if (!this.userScene) return;

    // ── independent mode ────────────────────────────────────────────
    // Every peer runs its own local simulation. Snapshots are NOT
    // exchanged. Score / event synchronization happens via sdk.emit() +
    // sdk.on() (custom events routed through Sync.emitEvent), and the
    // optional Leaderboard helper wraps that pattern.
    if (this.simMode === 'independent') {
      const myInput = this.sdkInput.serialize();
      const inputs = { [this.localPlayerId]: myInput };

      if (this.replay && this.replay.recording) {
        this.replay.recordFrame(stepIndex, inputs);
      }

      this.userScene.tick(dtSec, inputs);
      return;
    }

    // ── synchronized mode (default, authoritative-host) ─────────────
    if (this.net.isHost) {
      // Host: drain inputs from all peers, advance simulation, broadcast snapshot.
      const inputs = this.sync.hostDrainInputs();
      // The host's own input was never sent over the wire — read it now.
      inputs['host'] = this.sdkInput.serialize();

      if (this.replay && this.replay.recording) {
        this.replay.recordFrame(stepIndex, inputs);
      }

      this.userScene.tick(dtSec, inputs);

      const nowMs = performance.now();
      const snap = this.userScene.snapshot ? this.userScene.snapshot() : null;
      if (snap) {
        this.sync.hostMaybeBroadcastSnapshot(nowMs, stepIndex, snap);
      }
    } else {
      // Client: send local input to host. The host's authoritative snapshot
      // arrives via sync.onSnapshot and is applied directly.
      const myInput = this.sdkInput.serialize();
      this.sync.clientSendInput(stepIndex, myInput);

      // Optional: run client-side prediction by ticking the local sim too.
      // The user's GameScene can choose to ignore client ticks if it wants
      // pure server-rendered state.
      if (this.userScene.tickClient) {
        this.userScene.tickClient(dtSec, myInput);
      }
    }
  }

  /**
   * Show a "3, 2, 1, GO!" overlay before kicking off the match tick.
   * Gives all peers a visible sync window and masks scene-init jitter.
   * Calls `onDone` when the GO flash finishes.
   */
  _runStartCountdown(onDone) {
    this.scene.bringToTop();
    const W = this.scale.width;
    const H = this.scale.height;
    const cx = W / 2;
    const cy = H / 2;

    const dim = this.add.rectangle(cx, cy, W, H, 0x000000, 0.45)
      .setInteractive()
      .setDepth(9000);

    const fontFamily = (Theme && Theme.font && Theme.font.family) || 'system-ui, sans-serif';
    const label = this.add.text(cx, cy, '3', {
      fontFamily: fontFamily,
      fontSize: Math.floor(Math.min(W, H) * 0.28) + 'px',
      color: '#ffffff',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 10
    }).setOrigin(0.5).setDepth(9001);

    const cleanup = () => {
      try { dim.destroy(); } catch (_) { /* ignore */ }
      try { label.destroy(); } catch (_) { /* ignore */ }
      // Send ourselves back down so UserGame receives pointer events.
      try { this.scene.sendToBack(); } catch (_) { /* ignore */ }
    };

    // Zoom-pop tween on each beat so players see a clear change.
    const pulse = (text, fontSize) => {
      label.setText(text);
      if (fontSize) label.setFontSize(fontSize);
      label.setScale(0.6);
      this.tweens.add({
        targets: label,
        scale: 1.0,
        duration: 250,
        ease: 'Back.easeOut'
      });
    };

    pulse('3');
    const t1 = this.time.delayedCall(900,  () => pulse('2'));
    const t2 = this.time.delayedCall(1800, () => pulse('1'));
    const t3 = this.time.delayedCall(2700, () => {
      pulse('GO!', Math.floor(Math.min(W, H) * 0.22) + 'px');
      if (this.audio) {
        try { this.audio._forceUnlock && this.audio._forceUnlock(); } catch (_) { /* ignore */ }
      }
    });
    const t4 = this.time.delayedCall(3300, () => {
      cleanup();
      if (typeof onDone === 'function') onDone();
    });

    // Abort hook — if the scene is destroyed mid-countdown (e.g. user
    // backs out), cancel the pending timers so they don't fire on a
    // torn-down scene.
    this.events.once('shutdown', () => {
      for (const t of [t1, t2, t3, t4]) { try { t.remove(); } catch (_) { /* ignore */ } }
      cleanup();
    });
  }

  _togglePause() {
    if (this._paused) {
      this._paused = false;
      if (this._pauseModal) { this._pauseModal.close(); this._pauseModal = null; }
      this.scene.resume('UserGame');
      return;
    }
    this._paused = true;
    this.scene.pause('UserGame');
    // UserGame is dynamically added after GameContainer, so it renders on
    // top of us by default. Bring this scene to the top of the scene list
    // so the pause modal (which lives in our display list) draws above the
    // frozen game instead of being intertwined with game sprites.
    this.scene.bringToTop();
    this._pauseModal = new Modal({
      scene: this,
      title: 'Paused',
      buttons: [
        { label: 'Resume', onClick: () => this._togglePause() },
        { label: 'Quit',   variant: 'danger', onClick: () => this._quitToMenu() }
      ]
    });
  }

  _quitToMenu() {
    this.tick.stop();
    if (this._pauseModal) this._pauseModal.close();
    // Stop any game music so the main menu isn't haunted by leftover loops.
    if (this.audio) this.audio.stopMusic();
    this.scene.stop('UserGame');
    this.scene.start('MainMenu');
  }

  _endMatch(result) {
    this.tick.stop();
    if (this.replay && this.replay.recording) this.replay.endRecording();
    this.telemetry.emit('match-ended', { gameId: this.manifest.id, result });
    this.scene.stop('UserGame');
    this.scene.start('GameOver', { result });
  }
}
